import { UUID } from '../types';
import { SceneNode } from '../core/SceneNode';

export abstract class Animation {
    // Animation identification
    public readonly id: UUID;
    public readonly name: string;
    public readonly type: string;
    
    // Animation state
    private _isActive: boolean = false;
    private _isPaused: boolean = false;
    private _startTime: number = 0;
    private _pauseTime: number = 0;
    private _totalPausedTime: number = 0;
    
    // Generic SceneNode reference
    private _target: SceneNode | null = null;
    
    // Animation loop settings
    public loop: boolean = true;
    public autoStart: boolean = false;
    
    constructor(name: string, type: string) {
        this.id = crypto.randomUUID();
        this.name = name;
        this.type = type;
    }

    // Set the animation target
    setTarget(target: SceneNode): void {
        this._target = target;
        
        // Auto-start if enabled
        if (this.autoStart && !this._isActive) {
            this.start();
        }
    }

    getTarget(): SceneNode | null {
        return this._target;
    }

    // Convenience method to get POI data is no longer valid,
    // as the animator is decoupled from POIData.
    
    // Animation lifecycle
    start(): void {
        if (this._isActive) return;
        
        this._isActive = true;
        this._isPaused = false;
        this._startTime = performance.now();
        this._totalPausedTime = 0;
        
        this.onStart();
    }

    stop(): void {
        if (!this._isActive) return;
        
        this._isActive = false;
        this._isPaused = false;
        
        this.onStop();
    }

    pause(): void {
        if (!this._isActive || this._isPaused) return;
        
        this._isPaused = true;
        this._pauseTime = performance.now();
        
        this.onPause();
    }

    resume(): void {
        if (!this._isActive || !this._isPaused) return;
        
        this._isPaused = false;
        this._totalPausedTime += performance.now() - this._pauseTime;
        
        this.onResume();
    }

    restart(): void {
        this.stop();
        this.start();
    }

    // Animation state queries
    isActive(): boolean {
        return this._isActive;
    }

    isPaused(): boolean {
        return this._isPaused;
    }

    isRunning(): boolean {
        return this._isActive && !this._isPaused;
    }

    // Time calculations
    getElapsedTime(): number {
        if (!this._isActive) return 0;
        
        const currentTime = performance.now();
        const rawElapsed = currentTime - this._startTime;
        
        if (this._isPaused) {
            return rawElapsed - (currentTime - this._pauseTime) - this._totalPausedTime;
        }
        
        return rawElapsed - this._totalPausedTime;
    }

    getElapsedTimeSeconds(): number {
        return this.getElapsedTime() / 1000;
    }

    // Main update method - called by animation system
    update(deltaTime: number): void {
        if (!this.isRunning() || !this._target) return;
        
        const elapsedTime = this.getElapsedTimeSeconds();
        
        // Call the specific animation implementation
        this.animate(elapsedTime, deltaTime);
        
        // Handle loop completion
        if (this.shouldCompleteLoop(elapsedTime)) {
            if (this.loop) {
                this.restart();
            } else {
                this.stop();
            }
        }
    }

    // Abstract methods to be implemented by specific animations
    protected abstract animate(elapsedTime: number, deltaTime: number): void;
    protected abstract shouldCompleteLoop(elapsedTime: number): boolean;

    // Lifecycle event handlers (can be overridden)
    protected onStart(): void {
        // Override in subclasses
    }

    protected onStop(): void {
        // Override in subclasses
        this.onReset();
    }

    protected onPause(): void {
        // Override in subclasses
    }

    protected onResume(): void {
        // Override in subclasses
    }

    protected onReset(): void {
        // Can be overridden in subclasses to reset specific state
    }

    // Helper methods for reading POI parameters are no longer needed here.
    // The SceneNodeAnimator is responsible for passing values in.

    // Serialization
    toJSON(): any {
        return {
            id: this.id,
            name: this.name,
            type: this.type,
            isActive: this._isActive,
            isPaused: this._isPaused,
            loop: this.loop,
            autoStart: this.autoStart,
            elapsedTime: this.getElapsedTime()
        };
    }

    // Factory method for creating animations from JSON
    static fromJSON(json: any): Animation {
        throw new Error('fromJSON must be implemented by animation subclasses');
    }

    // Cloning
    clone(): Animation {
        throw new Error('clone must be implemented by animation subclasses');
    }

    // Cleanup
    destroy(): void {
        this.stop();
        this._target = null;
    }
} 